"use client";

// Client-side store for the COMMAND v2 chat prototype. Sending a message
// appends the user message plus a pending assistant message; a ~1.2s timeout
// in the provider then "completes" the reply with canned content and
// "Continue the flow" suggestions. Everything persists to sessionStorage.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

import { buildCannedReply, nextId } from "./catalog";
import { buildSeedState } from "./seed";
import type { CommandV2ComposerApi } from "@/components/command-team-v2/types";
import type {
  CommandProtoState,
  ProtoAttachedFile,
  ProtoChatMessage,
  ProtoChatSession,
  ProtoCommandAgent,
  ProtoPreset,
  ProtoPresetCategory,
  ProtoPresetVisibility,
  ProtoProject,
} from "./types";

// ---------------------------------------------------------------------------
// Actions

type Action =
  | { type: "RESET" }
  | {
      /** Replace everything with the stored snapshot (post-mount hydration). */
      type: "RESTORE_STATE";
      state: CommandProtoState;
    }
  | { type: "NEW_CHAT"; sessionId: string; projectId?: string | null }
  | { type: "SELECT_CHAT"; sessionId: string }
  | {
      /** Clear the unread dot without selecting (drawer reads decoupled). */
      type: "MARK_CHAT_READ";
      sessionId: string;
    }
  | { type: "TOGGLE_PIN_CHAT"; sessionId: string }
  | {
      type: "SEND_MESSAGE";
      /** Session to append to — a new session is created when it doesn't exist. */
      sessionId: string;
      text: string;
      userMessageId: string;
      assistantMessageId: string;
      presetId?: string;
      attachedFiles?: ProtoAttachedFile[];
      /** False = leave activeSessionId/draftProjectId alone (drawer sends). */
      select?: boolean;
      /** Creation only: target project (undefined = the page's draftProjectId). */
      projectId?: string | null;
    }
  | {
      /** A scheduled run lands as a new (non-active) chat in a project. */
      type: "SCHEDULE_CHAT";
      sessionId: string;
      title: string;
      prompt: string;
      projectId: string | null;
      userMessageId: string;
      assistantMessageId: string;
      /** Cadence + time as picked in the schedule dialog. */
      cadenceLabel: string;
      time: string;
    }
  | {
      /** Edit (or null = delete) the schedule behind a fromSchedule chat. */
      type: "UPDATE_CHAT_SCHEDULE";
      sessionId: string;
      schedule: { cadenceLabel: string; time: string; prompt: string; paused?: boolean } | null;
    }
  | {
      /** One run of an existing schedule — lands as its own chat pointing
       *  back at the anchor via scheduleSourceId. */
      type: "RUN_SCHEDULE";
      anchorId: string;
      sessionId: string;
      title: string;
      userMessageId: string;
      assistantMessageId: string;
    }
  | {
      type: "COMPLETE_REPLY";
      sessionId: string;
      messageId: string;
      text: string;
      presetId?: string;
      chainStepIndex?: number;
    }
  | { type: "RENAME_CHAT"; sessionId: string; title: string }
  | { type: "DELETE_CHAT"; sessionId: string }
  | {
      /** Bulk delete from the all-chats page — one pass, one re-render. */
      type: "DELETE_CHATS";
      sessionIds: string[];
    }
  | { type: "MOVE_CHAT"; sessionId: string; projectId: string | null }
  | { type: "CREATE_PROJECT"; projectId: string; name: string }
  | {
      /** Project landing view: no active chat, composer targets the project. */
      type: "SELECT_PROJECT";
      projectId: string;
    }
  | { type: "RENAME_PROJECT"; projectId: string; name: string }
  | {
      /** Project settings dialog — name, description, instructions, visibility. */
      type: "UPDATE_PROJECT";
      projectId: string;
      patch: Partial<Omit<ProtoProject, "id">>;
    }
  | {
      /** Takes the projects' chats with them — callers confirm first. */
      type: "DELETE_PROJECT";
      projectIds: string[];
    }
  | {
      /** Drag-and-drop in the sidebar: the project takes the target's slot. */
      type: "REORDER_PROJECT";
      projectId: string;
      targetProjectId: string;
    }
  | {
      /** Drag-and-drop in the sidebar: the agent takes the target's slot. */
      type: "REORDER_AGENT";
      agentId: string;
      targetAgentId: string;
    }
  | {
      /** Adopt a whole ordering at once — a sorted view becoming the custom
          order. Ids not in the list keep their agents at the end. */
      type: "SET_AGENT_ORDER";
      agentIds: string[];
    }
  | {
      /** Pick the agent new messages run through — exactly one is always picked. */
      type: "SELECT_AGENT";
      agentId: string;
    }
  | { type: "TOGGLE_FAVOURITE"; presetId: string }
  | { type: "MOVE_PRESET"; presetId: string; direction: "up" | "down" }
  | {
      /** Drag-and-drop: the preset takes the target's position in the global
       *  order (system presets are reorderable but never editable). */
      type: "REORDER_PRESET";
      presetId: string;
      targetPresetId: string;
    }
  | { type: "CREATE_PRESET"; preset: ProtoPreset }
  | {
      type: "UPDATE_PRESET";
      presetId: string;
      patch: Partial<Omit<ProtoPreset, "id">>;
    }
  | { type: "DELETE_PRESET"; presetId: string }
  | { type: "CREATE_AGENT"; agent: ProtoCommandAgent }
  | {
      type: "UPDATE_AGENT";
      agentId: string;
      patch: Partial<Omit<ProtoCommandAgent, "id">>;
    }
  | { type: "TOGGLE_AGENT_ACTIVE"; agentId: string }
  | { type: "DELETE_AGENT"; agentId: string };

// ---------------------------------------------------------------------------
// Reducer helpers

function deriveTitle(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > 48 ? `${clean.slice(0, 48)}…` : clean || "New chat";
}

/** Chat-list order everywhere: pinned first, then most recently updated. */
export function compareSessions(a: ProtoChatSession, b: ProtoChatSession): number {
  if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
  const aTime = a.updatedAt ?? a.createdAt ?? "";
  const bTime = b.updatedAt ?? b.createdAt ?? "";
  return bTime.localeCompare(aTime);
}

/**
 * Pure drop-reorder: the dragged preset takes the target's position in the
 * global order (any card can swap with any other — the category chip never
 * changes, only the sequence). Returns null when the drop changes nothing
 * (unknown ids, self). Also used by the Presets Library to preview the new
 * order live while dragging.
 */
/**
 * Pure drop-reorder for the sidebar lists (projects, agents): the dragged item
 * takes the target's slot. Neither carries an order field — the array IS the
 * order — so this is a plain splice. Returns null when the drop changes
 * nothing (unknown ids, self). Also used by the sidebars to preview the new
 * order live while dragging, so preview and result can never disagree.
 *
 * Agents are filtered before display ("shown in sidebar" only) but reordered
 * in the full array, which keeps the relative order of the visible ones right
 * without the hidden entries jumping around.
 */
export function reorderByDrop<T extends { id: string }>(
  items: T[],
  itemId: string,
  targetItemId: string
): T[] | null {
  if (itemId === targetItemId) return null;
  const fromIndex = items.findIndex((item) => item.id === itemId);
  const toIndex = items.findIndex((item) => item.id === targetItemId);
  if (fromIndex < 0 || toIndex < 0) return null;
  const arranged = [...items];
  const [moved] = arranged.splice(fromIndex, 1);
  arranged.splice(toIndex, 0, moved);
  return arranged;
}

export function reorderPresetsByDrop(
  presets: ProtoPreset[],
  presetId: string,
  targetPresetId: string
): ProtoPreset[] | null {
  const preset = presets.find((item) => item.id === presetId);
  const target = presets.find((item) => item.id === targetPresetId);
  if (!preset || !target || preset.id === target.id) return null;
  const sorted = [...presets].sort((a, b) => a.order - b.order);
  const orderSlots = sorted.map((item) => item.order);
  const fromIndex = sorted.findIndex((item) => item.id === preset.id);
  const toIndex = sorted.findIndex((item) => item.id === target.id);
  if (fromIndex < 0 || toIndex < 0) return null;
  const arranged = [...sorted];
  arranged.splice(fromIndex, 1);
  arranged.splice(toIndex, 0, preset);
  const orderById = new Map(
    arranged.map((item, index) => [item.id, orderSlots[index]])
  );
  return presets.map((item) => {
    const order = orderById.get(item.id);
    return order === undefined || order === item.order
      ? item
      : { ...item, order };
  });
}

function patchSession(
  state: CommandProtoState,
  sessionId: string,
  patch: (session: ProtoChatSession) => ProtoChatSession
): CommandProtoState {
  return {
    ...state,
    sessions: state.sessions.map((session) =>
      session.id === sessionId
        ? { ...patch(session), updatedAt: new Date().toISOString() }
        : session
    ),
  };
}

// ---------------------------------------------------------------------------
// Reducer

function reducer(state: CommandProtoState, action: Action): CommandProtoState {
  switch (action.type) {
    case "RESET":
      return buildSeedState();

    case "RESTORE_STATE":
      return action.state;

    case "NEW_CHAT": {
      // Creating a chat is immediate — an "Untitled chat" appears in the list
      // right away; the first message retitles it.
      const nowIso = new Date().toISOString();
      const session: ProtoChatSession = {
        id: action.sessionId,
        title: "Untitled chat",
        projectId: action.projectId ?? null,
        // stamped at birth so the chat remembers its agent from the start
        agentId: state.activeAgentId ?? null,
        messages: [],
        createdAt: nowIso,
        updatedAt: nowIso,
      };
      return {
        ...state,
        sessions: [session, ...state.sessions],
        activeSessionId: session.id,
        draftProjectId: action.projectId ?? null,
      };
    }

    case "SELECT_CHAT": {
      const opened = state.sessions.find(
        (session) => session.id === action.sessionId
      );
      // each chat remembers the agent it ran through — reopening it brings
      // that agent back instead of keeping whichever was last picked
      const rememberedAgentId =
        opened?.agentId &&
        state.agents.some((agent) => agent.id === opened.agentId)
          ? opened.agentId
          : null;
      return {
        ...state,
        activeSessionId: action.sessionId,
        activeAgentId: rememberedAgentId ?? state.activeAgentId,
        // opening the chat reads it — deliberately no updatedAt bump (a plain
        // select must not reorder the list)
        sessions: state.sessions.map((session) => {
          if (session.id !== action.sessionId) return session;
          const next = session.unread ? { ...session, unread: false } : session;
          // chats from before per-chat agents (no stamp, or a stamp pointing
          // at a deleted agent) adopt the current agent on first open, so
          // they remember from here on
          if (!rememberedAgentId && state.activeAgentId)
            return { ...next, agentId: state.activeAgentId };
          return next;
        }),
      };
    }

    case "MARK_CHAT_READ":
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.sessionId && session.unread
            ? { ...session, unread: false }
            : session
        ),
      };

    case "TOGGLE_PIN_CHAT":
      // no updatedAt bump — pinning must not shuffle the recency order
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.sessionId
            ? { ...session, pinned: !session.pinned }
            : session
        ),
      };

    case "SEND_MESSAGE": {
      const nowIso = new Date().toISOString();
      const userMessage: ProtoChatMessage = {
        id: action.userMessageId,
        role: "user",
        content: action.text,
        createdAt: nowIso,
        attachedFiles: action.attachedFiles?.length
          ? action.attachedFiles
          : undefined,
      };
      const pendingReply: ProtoChatMessage = {
        id: action.assistantMessageId,
        role: "assistant",
        content: "",
        createdAt: nowIso,
        pending: true,
      };

      const select = action.select !== false;
      const existing = state.sessions.find(
        (session) => session.id === action.sessionId
      );
      if (!existing) {
        const session: ProtoChatSession = {
          id: action.sessionId,
          title: deriveTitle(action.text),
          // drawer sends carry their own target project; page sends consume
          // the composer's draft project
          projectId:
            action.projectId !== undefined
              ? action.projectId
              : state.draftProjectId ?? null,
          agentId: state.activeAgentId ?? null,
          messages: [userMessage, pendingReply],
          createdAt: nowIso,
          updatedAt: nowIso,
        };
        return {
          ...state,
          sessions: [session, ...state.sessions],
          activeSessionId: select ? session.id : state.activeSessionId,
          draftProjectId: select ? null : state.draftProjectId,
        };
      }

      return {
        ...patchSession(state, action.sessionId, (session) => ({
          ...session,
          title:
            session.title === "Untitled chat" && session.messages.length === 0
              ? deriveTitle(action.text)
              : session.title,
          messages: [...session.messages, userMessage, pendingReply],
          // writing into a chat implies having read it
          unread: false,
          // composer sends into the open chat adopt the picked agent;
          // background sends (drawer, "Run now") keep the chat's own agent
          agentId:
            state.activeSessionId === action.sessionId
              ? state.activeAgentId ?? session.agentId ?? null
              : session.agentId ?? state.activeAgentId ?? null,
        })),
        activeSessionId: select ? action.sessionId : state.activeSessionId,
      };
    }

    case "SCHEDULE_CHAT": {
      const nowIso = new Date().toISOString();
      const session: ProtoChatSession = {
        id: action.sessionId,
        title: action.title,
        projectId: action.projectId,
        fromSchedule: true,
        schedule: {
          cadenceLabel: action.cadenceLabel,
          time: action.time,
          prompt: action.prompt,
        },
        agentId: state.activeAgentId ?? null,
        createdAt: nowIso,
        updatedAt: nowIso,
        messages: [
          {
            id: action.userMessageId,
            role: "user",
            content: action.prompt,
            createdAt: nowIso,
          },
          {
            id: action.assistantMessageId,
            role: "assistant",
            content: "",
            createdAt: nowIso,
            pending: true,
          },
        ],
      };
      return { ...state, sessions: [session, ...state.sessions] };
    }

    case "UPDATE_CHAT_SCHEDULE": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.sessionId
            ? action.schedule
              ? { ...session, fromSchedule: true, schedule: action.schedule }
              : // delete: the chat stays, only the recurring run goes
                { ...session, fromSchedule: false, schedule: undefined }
            : session
        ),
      };
    }

    case "RUN_SCHEDULE": {
      const anchor = state.sessions.find(
        (session) => session.id === action.anchorId
      );
      if (!anchor?.schedule) return state;
      const nowIso = new Date().toISOString();
      // the run inherits the anchor's agent and project but is its own chat
      const run: ProtoChatSession = {
        id: action.sessionId,
        title: action.title,
        projectId: anchor.projectId ?? null,
        fromSchedule: true,
        scheduleSourceId: anchor.id,
        agentId: anchor.agentId ?? null,
        createdAt: nowIso,
        updatedAt: nowIso,
        messages: [
          {
            id: action.userMessageId,
            role: "user",
            content: anchor.schedule.prompt,
            createdAt: nowIso,
          },
          {
            id: action.assistantMessageId,
            role: "assistant",
            content: "",
            createdAt: nowIso,
            pending: true,
          },
        ],
      };
      return { ...state, sessions: [run, ...state.sessions] };
    }

    case "COMPLETE_REPLY": {
      const session = state.sessions.find((item) => item.id === action.sessionId);
      if (!session) return state;
      const message = session.messages.find((item) => item.id === action.messageId);
      if (!message || !message.pending) return state;

      const reply = buildCannedReply(action.text, action.presetId);

      // Chain-aware: when this message came from a chain preset step, point
      // the suggestions row at the next step (until the chain is done).
      let chainNext: ProtoChatMessage["chainNext"];
      const preset = action.presetId
        ? state.presets.find((item) => item.id === action.presetId)
        : undefined;
      if (preset?.chain && action.chainStepIndex !== undefined) {
        const next = action.chainStepIndex + 1;
        if (next < preset.chain.steps.length) {
          chainNext = { presetId: preset.id, stepIndex: next };
        }
      }

      return patchSession(state, action.sessionId, (current) => ({
        ...current,
        // finished in a chat the user isn't looking at → unread until selected
        unread: state.activeSessionId !== action.sessionId ? true : current.unread,
        messages: current.messages.map((item) =>
          item.id === action.messageId
            ? {
                ...item,
                pending: false,
                content: reply.content,
                suggestions: reply.suggestions,
                chainNext,
                createdAt: new Date().toISOString(),
              }
            : item
        ),
      }));
    }

    case "RENAME_CHAT":
      return patchSession(state, action.sessionId, (session) => ({
        ...session,
        title: action.title,
      }));

    case "DELETE_CHAT":
      return {
        ...state,
        sessions: state.sessions.filter((session) => session.id !== action.sessionId),
        activeSessionId:
          state.activeSessionId === action.sessionId ? null : state.activeSessionId,
        // deleting the open chat lands on the empty state, not a stale
        // project view (project view = no active chat + draftProjectId set)
        draftProjectId:
          state.activeSessionId === action.sessionId ? null : state.draftProjectId,
      };

    case "DELETE_CHATS": {
      const doomed = new Set(action.sessionIds);
      if (doomed.size === 0) return state;
      return {
        ...state,
        sessions: state.sessions.filter((session) => !doomed.has(session.id)),
        activeSessionId:
          state.activeSessionId && doomed.has(state.activeSessionId)
            ? null
            : state.activeSessionId,
        draftProjectId:
          state.activeSessionId && doomed.has(state.activeSessionId)
            ? null
            : state.draftProjectId,
      };
    }

    case "MOVE_CHAT":
      return patchSession(state, action.sessionId, (session) => ({
        ...session,
        projectId: action.projectId,
      }));

    case "CREATE_PROJECT":
      return {
        ...state,
        projects: [...state.projects, { id: action.projectId, name: action.name }],
      };

    case "SELECT_PROJECT":
      return {
        ...state,
        activeSessionId: null,
        draftProjectId: action.projectId,
      };

    case "SELECT_AGENT":
      return {
        ...state,
        activeAgentId: action.agentId,
        // the picked agent always appears in the sidebar list
        agents: state.agents.map((agent) =>
          agent.id === action.agentId && !agent.isActive
            ? { ...agent, isActive: true }
            : agent
        ),
        // an explicit pick while a chat is open re-stamps that chat, so
        // coming back to it later restores this choice
        sessions: state.activeSessionId
          ? state.sessions.map((session) =>
              session.id === state.activeSessionId
                ? { ...session, agentId: action.agentId }
                : session
            )
          : state.sessions,
      };

    case "UPDATE_PROJECT":
      return {
        ...state,
        projects: state.projects.map((project) =>
          project.id === action.projectId
            ? { ...project, ...action.patch }
            : project
        ),
      };

    case "RENAME_PROJECT":
      return {
        ...state,
        projects: state.projects.map((project) =>
          project.id === action.projectId
            ? { ...project, name: action.name }
            : project
        ),
      };

    case "DELETE_PROJECT": {
      const doomed = new Set(action.projectIds);
      if (doomed.size === 0) return state;
      // The folder takes its chats with it. Callers confirm first — the count
      // is in the dialog, because this is not recoverable in the prototype.
      const sessions = state.sessions.filter(
        (session) => !(session.projectId && doomed.has(session.projectId))
      );
      const activeSurvives = sessions.some(
        (session) => session.id === state.activeSessionId
      );
      return {
        ...state,
        projects: state.projects.filter((project) => !doomed.has(project.id)),
        sessions,
        activeSessionId: activeSurvives ? state.activeSessionId : null,
        draftProjectId:
          state.draftProjectId && doomed.has(state.draftProjectId)
            ? null
            : activeSurvives
              ? state.draftProjectId
              : null,
      };
    }

    case "REORDER_PROJECT": {
      const projects = reorderByDrop(
        state.projects,
        action.projectId,
        action.targetProjectId
      );
      return projects ? { ...state, projects } : state;
    }

    case "REORDER_AGENT": {
      const agents = reorderByDrop(
        state.agents,
        action.agentId,
        action.targetAgentId
      );
      return agents ? { ...state, agents } : state;
    }

    case "SET_AGENT_ORDER": {
      const byId = new Map(state.agents.map((agent) => [agent.id, agent]));
      const ordered = action.agentIds
        .map((id) => byId.get(id))
        .filter((agent): agent is ProtoCommandAgent => Boolean(agent));
      const leftover = state.agents.filter(
        (agent) => !action.agentIds.includes(agent.id)
      );
      return { ...state, agents: [...ordered, ...leftover] };
    }

    case "TOGGLE_FAVOURITE":
      return {
        ...state,
        presets: state.presets.map((preset) =>
          preset.id === action.presetId
            ? { ...preset, favourite: !preset.favourite }
            : preset
        ),
      };

    case "MOVE_PRESET": {
      const preset = state.presets.find((item) => item.id === action.presetId);
      if (!preset) return state;
      const siblings = state.presets
        .filter((item) => item.category === preset.category)
        .sort((a, b) => a.order - b.order);
      const index = siblings.findIndex((item) => item.id === preset.id);
      const swapWith =
        action.direction === "up" ? siblings[index - 1] : siblings[index + 1];
      if (!swapWith) return state;
      return {
        ...state,
        presets: state.presets.map((item) => {
          if (item.id === preset.id) return { ...item, order: swapWith.order };
          if (item.id === swapWith.id) return { ...item, order: preset.order };
          return item;
        }),
      };
    }

    case "REORDER_PRESET": {
      const presets = reorderPresetsByDrop(
        state.presets,
        action.presetId,
        action.targetPresetId
      );
      return presets ? { ...state, presets } : state;
    }

    case "CREATE_PRESET":
      return { ...state, presets: [...state.presets, action.preset] };

    case "UPDATE_PRESET":
      return {
        ...state,
        presets: state.presets.map((preset) =>
          preset.id === action.presetId
            ? { ...preset, ...action.patch }
            : preset
        ),
      };

    case "DELETE_PRESET":
      return {
        ...state,
        presets: state.presets.filter(
          (preset) => preset.id !== action.presetId
        ),
      };

    case "CREATE_AGENT":
      return {
        ...state,
        agents: [...state.agents, action.agent],
        // the first agent to exist becomes the picked one
        activeAgentId: state.activeAgentId ?? action.agent.id,
      };

    case "UPDATE_AGENT":
      return {
        ...state,
        agents: state.agents.map((agent) =>
          agent.id === action.agentId ? { ...agent, ...action.patch } : agent
        ),
      };

    // isActive = shown in the sidebar list; the toggle never touches whether
    // the agent can answer — that's the single picked agent (activeAgentId)
    case "TOGGLE_AGENT_ACTIVE": {
      const target = state.agents.find((agent) => agent.id === action.agentId);
      if (!target) return state;
      const hiding = target.isActive;
      const fallback = state.agents.find(
        (agent) => agent.isActive && agent.id !== action.agentId
      );
      // the sidebar always keeps at least one agent
      if (hiding && !fallback) return state;
      return {
        ...state,
        agents: state.agents.map((agent) =>
          agent.id === action.agentId
            ? { ...agent, isActive: !agent.isActive }
            : agent
        ),
        // hiding the picked agent hands the pick to the next visible one
        activeAgentId:
          hiding && state.activeAgentId === action.agentId
            ? fallback!.id
            : state.activeAgentId,
      };
    }

    case "DELETE_AGENT": {
      const agents = state.agents.filter((agent) => agent.id !== action.agentId);
      if (state.activeAgentId !== action.agentId) return { ...state, agents };
      // deleting the picked agent hands the pick to the next agent (shown
      // first) — and whoever inherits it appears in the sidebar
      const next = agents.find((agent) => agent.isActive) ?? agents[0] ?? null;
      return {
        ...state,
        agents:
          next && !next.isActive
            ? agents.map((agent) =>
                agent.id === next.id ? { ...agent, isActive: true } : agent
              )
            : agents,
        activeAgentId: next?.id ?? null,
      };
    }

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context + persistence

interface CommandPrototypeContextValue {
  state: CommandProtoState;
  dispatch: React.Dispatch<Action>;
  /** Always-current state for use inside event handlers. */
  stateRef: React.MutableRefObject<CommandProtoState>;
  /** False until the stored snapshot replaced the seed (post-mount). */
  hydrated: boolean;
  /** When set, `useCommandPrototype` returns this instead of the demo store. */
  apiStore?: CommandPrototypeStoreResult | null;
}

export interface CommandProtoActions {
  reset: () => void;
  newChat: (projectId?: string | null) => void;
  selectChat: (sessionId: string) => void;
  sendMessage: (
    text: string,
    options?: {
      presetId?: string;
      chainStepIndex?: number;
      attachedFiles?: ProtoAttachedFile[];
      sessionId?: string | null;
      selectSession?: boolean;
      projectId?: string | null;
      selection_value?: string;
      selection_tool_call_id?: string;
    }
  ) => string | null;
  scheduleChat: (input: {
    prompt: string;
    title: string;
    projectId: string | null;
    cadenceLabel?: string;
    time?: string;
  }) => void;
  runScheduleNow: (anchorId: string) => string | null;
  renameChat: (sessionId: string, title: string) => void;
  markChatRead: (sessionId: string) => void;
  togglePinChat: (sessionId: string) => void;
  deleteChat: (sessionId: string) => void;
  deleteChats: (sessionIds: string[]) => void;
  moveChatToProject: (sessionId: string, projectId: string | null) => void;
  createProject: (name: string) => string;
  selectProject: (projectId: string) => void;
  selectAgent: (agentId: string) => void;
  renameProject: (projectId: string, name: string) => void;
  updateProject: (
    projectId: string,
    patch: Partial<Omit<ProtoProject, "id">>
  ) => void;
  deleteProjects: (projectIds: string[]) => void;
  reorderProject: (projectId: string, targetProjectId: string) => void;
  reorderAgent: (agentId: string, targetAgentId: string) => void;
  setAgentOrder: (agentIds: string[]) => void;
  toggleFavourite: (presetId: string) => void;
  movePreset: (presetId: string, direction: "up" | "down") => void;
  reorderPreset: (presetId: string, targetPresetId: string) => void;
  createPreset: (input: {
    title: string;
    promptText: string;
    category?: ProtoPresetCategory;
    description?: string;
    agentId?: string | null;
    visibility?: ProtoPresetVisibility;
    tags?: string[];
  }) => void;
  updatePreset: (
    presetId: string,
    patch: Partial<Omit<ProtoPreset, "id">>
  ) => void;
  deletePreset: (presetId: string) => void;
  createCommandAgent: (
    input: Pick<ProtoCommandAgent, "name" | "role" | "description" | "persona" | "restrictions"> &
      Partial<Omit<ProtoCommandAgent, "id" | "isActive">>
  ) => string;
  updateCommandAgent: (
    agentId: string,
    patch: Partial<Omit<ProtoCommandAgent, "id">>
  ) => void;
  toggleCommandAgentActive: (agentId: string) => void;
  deleteCommandAgent: (agentId: string) => void;
  updateChatSchedule: (
    sessionId: string,
    schedule: {
      cadenceLabel: string;
      time: string;
      prompt: string;
      paused?: boolean;
    } | null
  ) => void;
}

export interface CommandV2LoadingState {
  agents: boolean;
  projects: boolean;
  /** Unfiled / “Other chats” list only — not project folders. */
  sessions: boolean;
  presets: boolean;
}

export interface CommandPrototypeStoreResult {
  state: CommandProtoState;
  actions: CommandProtoActions;
  activeSession: ProtoChatSession | null;
  hydrated: boolean;
  /** Per-section loading flags for skeleton UI (API-backed store only). */
  loading?: CommandV2LoadingState;
  /** Agent id while POST /activate is in flight (API-backed store only). */
  activatingAgentId?: string | null;
  /** True while the active session's first message page is loading (API-backed store only). */
  isLoadingThread?: boolean;
  /** Sentinel ref for loading older messages when scrolling to the top of the thread. */
  loadMoreRef?: (node: Element | null) => void;
  isFetchingNextPage?: boolean;
  /** Sentinel ref for loading more sidebar / History chats on scroll. */
  sessionsLoadMoreRef?: (node: Element | null) => void;
  hasMoreSessions?: boolean;
  isFetchingMoreSessions?: boolean;
  hasMorePresets?: boolean;
  isFetchingMorePresets?: boolean;
  loadMorePresets?: () => void;
  composer?: CommandV2ComposerApi;
  regenerateMessage?: (messageId: string) => void;
  isRegenerating?: boolean;
  getAssistantBranchCount?: (messageId: string) => number;
  threadMessages?: import("@/components/aios-command/chat/chat-types").ExtendedChatMessage[];
  changeMessageBranch?: (messageId: string, branchIndex: number) => void;
  sessionIdForMessages?: string | null;
  handleStopGeneration?: () => void;
  isStopping?: boolean;
  isTyping?: boolean;
  streamingMessageId?: string | null;
  streamingPhase?: "working" | "responding";
  stickyContextRef?: React.MutableRefObject<any>;
  contextUsage?: import("@/types/command-agent").ContextUsage | null;
}

export const CommandPrototypeContext =
  createContext<CommandPrototypeContextValue | null>(null);

// Exported so the global Talk to Command sidebar can read the same state
// (chat list + presets) from outside the provider.
export const COMMAND_PROTO_STORAGE_KEY = "aios-commandv2-proto-state-v2";
const STORAGE_KEY = COMMAND_PROTO_STORAGE_KEY;

/**
 * Set the chat page's landing selection straight in storage — used by v2 pages
 * that render the Command panel outside the chat page (Agents, settings) right
 * before they router.push back to the chat. Dispatching + navigating instead
 * would race the provider's persistence effect against the unmount.
 */
export function primeCommandProtoSelection(patch: {
  activeSessionId?: string | null;
  draftProjectId?: string | null;
}) {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as CommandProtoState;
    const next: CommandProtoState = { ...parsed, ...patch };
    // opening a chat this way reads it and restores/adopts the chat's agent,
    // same as SELECT_CHAT
    if (patch.activeSessionId) {
      const opened = parsed.sessions?.find(
        (session) => session.id === patch.activeSessionId
      );
      const rememberedAgentId =
        opened?.agentId &&
        parsed.agents?.some((agent) => agent.id === opened.agentId)
          ? opened.agentId
          : null;
      if (rememberedAgentId) next.activeAgentId = rememberedAgentId;
      next.sessions = parsed.sessions?.map((session) => {
        if (session.id !== patch.activeSessionId) return session;
        const read = session.unread ? { ...session, unread: false } : session;
        // unstamped chats adopt the current agent on open, so they remember
        if (!rememberedAgentId && parsed.activeAgentId)
          return { ...read, agentId: parsed.activeAgentId };
        return read;
      });
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore — the chat page just opens on its own state */
  }
}

// Fired (on window) after each persist, so outside readers can refresh…
export const COMMAND_PROTO_UPDATED_EVENT = "aios:command-proto-updated";
// …and dispatched (on window) by outside surfaces to select a chat while the
// Command page is mounted; carries the sessionId as detail.
export const COMMAND_PROTO_SELECT_EVENT = "aios:command-proto-select-chat";

/** One agent is always picked: the stored pick if it's still shown, else the
 *  first shown agent, else the first agent at all (empty roster → null). */
function resolveActiveAgentId(
  agents: ProtoCommandAgent[],
  candidate: string | null | undefined
): string | null {
  const stored = candidate
    ? agents.find((agent) => agent.id === candidate)
    : undefined;
  if (stored?.isActive) return stored.id;
  return (
    agents.find((agent) => agent.isActive)?.id ?? agents[0]?.id ?? null
  );
}

function initState(): CommandProtoState {
  if (typeof window === "undefined") return buildSeedState();
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as CommandProtoState;
      if (parsed?.sessions && parsed?.presets) {
        const seed = buildSeedState();
        // Stored state may predate the isDefault field — stamp the seed's
        // default agent and pin it back to personal.
        const seedDefaultId = seed.agents.find((item) => item.isDefault)?.id;
        const storedAgents = parsed.agents ?? seed.agents;
        const agents = storedAgents.map((agent, index) => {
          const isDefault = agent.isDefault ?? agent.id === seedDefaultId;
          return {
          ...agent,
          isDefault,
          // stored state predates appIssues — stamp the seed's demo issue on
          // its agent once; after a "reconnect" the empty array sticks
          appIssues:
            agent.appIssues ??
            seed.agents.find((item) => item.id === agent.id)?.appIssues,
          visibility: isDefault ? ("personal" as const) : agent.visibility,
          // Stored state may predate createdAt — stamp from the array index
          // (agents are appended on create, so index ≈ creation order),
          // otherwise the Newest/Oldest sorts have nothing to go on.
          createdAt:
            agent.createdAt ??
            new Date(
              Date.now() - (storedAgents.length - index) * 60_000
            ).toISOString(),
          llmConfig: agent.llmConfig ?? {
            model: "Claude Opus 4.7",
            temperature: 0.2,
            maxTokens: 8192,
            timeoutSec: 120,
          },
          connectedApps: agent.connectedApps ?? [],
          customApps: agent.customApps ?? [],
          knowledgeBases: agent.knowledgeBases ?? [],
          creditLimit: agent.creditLimit ?? { amount: 10000, period: "week" as const },
          };
        });
        // One-time: append seed agents this stored state has never seen
        // (added after it was first saved). The flag makes it once-only, so
        // deleting one of them doesn't resurrect it on the next reload.
        let mergedAgents: ProtoCommandAgent[] = agents;
        if (!parsed.seededAgentsV2) {
          const knownIds = new Set(agents.map((item) => item.id));
          mergedAgents = [
            ...agents,
            ...seed.agents.filter((item) => !knownIds.has(item.id)),
          ];
        }
        return {
          presets: parsed.presets ?? seed.presets,
          projects: parsed.projects ?? seed.projects,
          agents: mergedAgents,
          seededAgentsV2: true,
          activeSessionId: parsed.activeSessionId ?? null,
          draftProjectId: parsed.draftProjectId ?? null,
          activeAgentId: resolveActiveAgentId(mergedAgents, parsed.activeAgentId),
          // Pending replies don't survive a refresh — drop them. The chat
          // being restored on screen counts as read.
          sessions: parsed.sessions.map((session) => ({
            ...session,
            unread:
              session.id === (parsed.activeSessionId ?? null)
                ? false
                : session.unread,
            messages: session.messages.filter((message) => !message.pending),
          })),
        };
      }
    }
  } catch {
    // fall through to seed
  }
  return buildSeedState();
}

const REPLY_DELAY_MS = 1200;

export function CommandPrototypeProvider({ children }: { children: ReactNode }) {
  // Seed first (matches the server render), then swap in the stored state
  // after mount — the provider now lives in the root layout, so it must render
  // children immediately instead of null-gating the whole app.
  const [state, dispatch] = useReducer(reducer, undefined, buildSeedState);
  const [mounted, setMounted] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    dispatch({ type: "RESTORE_STATE", state: initState() });
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // storage unavailable — in-memory only
    }
    // let outside readers (the Talk to Command sidebar) refresh their copy
    window.dispatchEvent(new CustomEvent(COMMAND_PROTO_UPDATED_EVENT));
  }, [state, mounted]);

  // The Talk to Command sidebar selects chats from outside the provider —
  // honour those while the page is mounted (when it isn't, the sidebar writes
  // activeSessionId straight to sessionStorage and initState picks it up).
  useEffect(() => {
    const onSelect = (event: Event) => {
      const sessionId = (event as CustomEvent<string>).detail;
      if (sessionId) dispatch({ type: "SELECT_CHAT", sessionId });
    };
    window.addEventListener(COMMAND_PROTO_SELECT_EVENT, onSelect);
    return () => window.removeEventListener(COMMAND_PROTO_SELECT_EVENT, onSelect);
  }, []);

  const value = useMemo(
    () => ({ state, dispatch, stateRef, hydrated: mounted }),
    [state, mounted]
  );

  return (
    <CommandPrototypeContext.Provider value={value}>
      {children}
    </CommandPrototypeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook

export function useCommandPrototype(): CommandPrototypeStoreResult {
  const context = useContext(CommandPrototypeContext);
  if (!context) {
    throw new Error(
      "useCommandPrototype must be used within CommandPrototypeProvider"
    );
  }
  if (context.apiStore) {
    return context.apiStore;
  }
  const { state, dispatch, stateRef, hydrated } = context;

  const actions = useMemo(
    () => ({
      reset: () => {
        try {
          window.sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
        dispatch({ type: "RESET" });
      },
      newChat: (projectId?: string | null) =>
        dispatch({ type: "NEW_CHAT", sessionId: nextId("chat"), projectId }),
      selectChat: (sessionId: string) =>
        dispatch({ type: "SELECT_CHAT", sessionId }),
      /**
       * Appends the user message + a pending assistant reply, then completes
       * the reply after a short "thinking" delay. Targets `options.sessionId`
       * when given (null = always create a new session — drawer sends), else
       * the active one. `selectSession: false` leaves the page's selection
       * untouched. Returns the session id written to.
       */
      sendMessage: (
        text: string,
        options?: {
          presetId?: string;
          chainStepIndex?: number;
          attachedFiles?: ProtoAttachedFile[];
          sessionId?: string | null;
          selectSession?: boolean;
          projectId?: string | null;
          selection_value?: string;
          selection_tool_call_id?: string;
        }
      ): string | null => {
        const trimmed = text.trim();
        if (!trimmed) return null;
        const sessionId =
          options?.sessionId !== undefined
            ? options.sessionId ?? nextId("chat")
            : stateRef.current.activeSessionId ?? nextId("chat");
        const userMessageId = nextId("msg");
        const assistantMessageId = nextId("msg");
        dispatch({
          type: "SEND_MESSAGE",
          sessionId,
          text: trimmed,
          userMessageId,
          assistantMessageId,
          presetId: options?.presetId,
          attachedFiles: options?.attachedFiles,
          select: options?.selectSession,
          projectId: options?.projectId,
        });
        window.setTimeout(() => {
          dispatch({
            type: "COMPLETE_REPLY",
            sessionId,
            messageId: assistantMessageId,
            text: trimmed,
            presetId: options?.presetId,
            chainStepIndex: options?.chainStepIndex,
          });
        }, REPLY_DELAY_MS);
        return sessionId;
      },
      /**
       * Simulates a scheduled run landing: creates a new fromSchedule chat
       * (in the given project, when any) whose reply completes shortly after,
       * without stealing focus from the current chat.
       */
      scheduleChat: (input: {
        prompt: string;
        title: string;
        projectId: string | null;
        cadenceLabel?: string;
        time?: string;
      }) => {
        const prompt = input.prompt.trim();
        if (!prompt) return;
        const sessionId = nextId("chat");
        const userMessageId = nextId("msg");
        const assistantMessageId = nextId("msg");
        dispatch({
          type: "SCHEDULE_CHAT",
          sessionId,
          title: input.title,
          prompt,
          projectId: input.projectId,
          userMessageId,
          assistantMessageId,
          cadenceLabel: input.cadenceLabel ?? "daily",
          time: input.time ?? "08:00",
        });
        window.setTimeout(() => {
          dispatch({
            type: "COMPLETE_REPLY",
            sessionId,
            messageId: assistantMessageId,
            text: prompt,
          });
        }, REPLY_DELAY_MS);
      },
      /**
       * One run of an existing schedule, landing as its own chat (titled
       * "<schedule> — <day>") whose reply completes shortly after. Returns
       * the run's session id, or null when the anchor has no schedule.
       */
      runScheduleNow: (anchorId: string): string | null => {
        const anchor = stateRef.current.sessions.find(
          (session) => session.id === anchorId
        );
        if (!anchor?.schedule) return null;
        const sessionId = nextId("chat");
        const userMessageId = nextId("msg");
        const assistantMessageId = nextId("msg");
        const dayLabel = new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        });
        dispatch({
          type: "RUN_SCHEDULE",
          anchorId,
          sessionId,
          title: `${anchor.title} — ${dayLabel}`,
          userMessageId,
          assistantMessageId,
        });
        window.setTimeout(() => {
          dispatch({
            type: "COMPLETE_REPLY",
            sessionId,
            messageId: assistantMessageId,
            text: anchor.schedule?.prompt ?? "",
          });
        }, REPLY_DELAY_MS);
        return sessionId;
      },
      renameChat: (sessionId: string, title: string) =>
        dispatch({ type: "RENAME_CHAT", sessionId, title }),
      markChatRead: (sessionId: string) =>
        dispatch({ type: "MARK_CHAT_READ", sessionId }),
      togglePinChat: (sessionId: string) =>
        dispatch({ type: "TOGGLE_PIN_CHAT", sessionId }),
      deleteChat: (sessionId: string) =>
        dispatch({ type: "DELETE_CHAT", sessionId }),
      deleteChats: (sessionIds: string[]) =>
        dispatch({ type: "DELETE_CHATS", sessionIds }),
      moveChatToProject: (sessionId: string, projectId: string | null) =>
        dispatch({ type: "MOVE_CHAT", sessionId, projectId }),
      createProject: (name: string) => {
        const projectId = nextId("proj");
        dispatch({ type: "CREATE_PROJECT", projectId, name });
        return projectId;
      },
      selectProject: (projectId: string) =>
        dispatch({ type: "SELECT_PROJECT", projectId }),
      selectAgent: (agentId: string) =>
        dispatch({ type: "SELECT_AGENT", agentId }),
      renameProject: (projectId: string, name: string) =>
        dispatch({ type: "RENAME_PROJECT", projectId, name }),
      updateProject: (
        projectId: string,
        patch: Partial<Omit<ProtoProject, "id">>
      ) => dispatch({ type: "UPDATE_PROJECT", projectId, patch }),
      /** Deletes the projects AND every chat inside them — confirm first. */
      deleteProjects: (projectIds: string[]) =>
        dispatch({ type: "DELETE_PROJECT", projectIds }),
      reorderProject: (projectId: string, targetProjectId: string) =>
        dispatch({ type: "REORDER_PROJECT", projectId, targetProjectId }),
      reorderAgent: (agentId: string, targetAgentId: string) =>
        dispatch({ type: "REORDER_AGENT", agentId, targetAgentId }),
      setAgentOrder: (agentIds: string[]) =>
        dispatch({ type: "SET_AGENT_ORDER", agentIds }),
      toggleFavourite: (presetId: string) =>
        dispatch({ type: "TOGGLE_FAVOURITE", presetId }),
      movePreset: (presetId: string, direction: "up" | "down") =>
        dispatch({ type: "MOVE_PRESET", presetId, direction }),
      reorderPreset: (presetId: string, targetPresetId: string) =>
        dispatch({ type: "REORDER_PRESET", presetId, targetPresetId }),
      createPreset: (input: {
        title: string;
        promptText: string;
        category?: ProtoPresetCategory;
        description?: string;
        agentId?: string | null;
        visibility?: ProtoPresetVisibility;
        tags?: string[];
      }) => {
        const preset: ProtoPreset = {
          id: nextId("preset"),
          title: input.title,
          promptText: input.promptText,
          description: input.description,
          category: input.category ?? "custom",
          favourite: false,
          order:
            Math.max(0, ...stateRef.current.presets.map((item) => item.order)) + 1,
          agentId: input.agentId ?? null,
          visibility: input.visibility,
          tags: input.tags,
        };
        dispatch({ type: "CREATE_PRESET", preset });
      },
      updatePreset: (
        presetId: string,
        patch: Partial<Omit<ProtoPreset, "id">>
      ) => dispatch({ type: "UPDATE_PRESET", presetId, patch }),
      deletePreset: (presetId: string) =>
        dispatch({ type: "DELETE_PRESET", presetId }),
      createCommandAgent: (
        input: Pick<ProtoCommandAgent, "name" | "role" | "description" | "persona" | "restrictions"> &
          Partial<Omit<ProtoCommandAgent, "id" | "isActive">>
      ) => {
        const agent: ProtoCommandAgent = {
          visibility: "personal",
          createdAt: new Date().toISOString(),
          llmConfig: { model: "Claude Opus 4.7", temperature: 0.2, maxTokens: 8192, timeoutSec: 120 },
          connectedApps: [],
          customApps: [],
          knowledgeBases: [],
          creditLimit: { amount: 10000, period: "week" },
          ...input,
          id: nextId("agent"),
          isActive: true,
        };
        dispatch({ type: "CREATE_AGENT", agent });
        return agent.id;
      },
      updateCommandAgent: (
        agentId: string,
        patch: Partial<Omit<ProtoCommandAgent, "id">>
      ) => dispatch({ type: "UPDATE_AGENT", agentId, patch }),
      toggleCommandAgentActive: (agentId: string) =>
        dispatch({ type: "TOGGLE_AGENT_ACTIVE", agentId }),
      deleteCommandAgent: (agentId: string) =>
        dispatch({ type: "DELETE_AGENT", agentId }),
      /** Edit (or null = delete) the schedule behind a fromSchedule chat. */
      updateChatSchedule: (
        sessionId: string,
        schedule: { cadenceLabel: string; time: string; prompt: string; paused?: boolean } | null
      ) => dispatch({ type: "UPDATE_CHAT_SCHEDULE", sessionId, schedule }),
    }),
    [dispatch, stateRef]
  );

  const activeSession = useMemo(
    () =>
      state.sessions.find((session) => session.id === state.activeSessionId) ??
      null,
    [state.sessions, state.activeSessionId]
  );

  return { state, actions, activeSession, hydrated };
}
